import { useState, useEffect } from 'react';
import { CreditCard, Loader2, Save, Key, Shield, Webhook, Eye, EyeOff } from 'lucide-react';
import { paymentGatewayService } from '../api/services/payment-gateway';
import { configurationsService } from '../api/services';
import type { PaymentGatewayConfig, PaymentGatewayProvider, UpsertPaymentGatewayRequest, Configuration } from '../types';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Skeleton } from './ui/skeleton';

const PAYMENT_PROVIDERS: { value: PaymentGatewayProvider; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'cashfree', label: 'Cashfree' },
  { value: 'razorpay', label: 'Razorpay' },
];

const PAYMENT_GATEWAY_CONFIG_KEY = 'PAYMENT_GATEWAY';

function isBooleanConfig(config: Configuration): boolean {
  return config.value === 'true' || config.value === 'false';
}

export function PaymentGatewaySettings() {
  const [gatewayConfig, setGatewayConfig] = useState<PaymentGatewayConfig | null>(null);
  const [paymentConfigs, setPaymentConfigs] = useState<Configuration[]>([]);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [selectedProvider, setSelectedProvider] = useState<PaymentGatewayProvider>('none');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [initialProvider, setInitialProvider] = useState<PaymentGatewayProvider>('none');
  const [savingOptions, setSavingOptions] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [gateway, allConfigs] = await Promise.all([
        paymentGatewayService.getPaymentGatewayConfig(),
        configurationsService.getConfigurations(),
      ]);

      if (gateway) {
        setGatewayConfig(gateway);
        setSelectedProvider(gateway.provider);
        setInitialProvider(gateway.provider);
        setApiKey(gateway.api_key || '');
        setApiKeyId(gateway.api_key_id || '');
        setApiSecret(gateway.api_secret || '');
        setWebhookSecret(gateway.webhook_secret || '');
      }

      const paymentGroup = allConfigs.filter((c) => c.config_group === 'payment');

      const gatewayConfigEntry = paymentGroup.find((c) => c.key === PAYMENT_GATEWAY_CONFIG_KEY);
      if (gatewayConfigEntry && !gateway) {
        const providerValue = gatewayConfigEntry.value as PaymentGatewayProvider;
        if (PAYMENT_PROVIDERS.some((p) => p.value === providerValue)) {
          setSelectedProvider(providerValue);
          setInitialProvider(providerValue);
        }
      }

      const displayConfigs = paymentGroup.filter((c) => c.key !== PAYMENT_GATEWAY_CONFIG_KEY);
      setPaymentConfigs(displayConfigs);
      const values: Record<string, string> = {};
      displayConfigs.forEach((c) => {
        values[c.key] = c.value;
      });
      setEditedValues(values);
    } catch (error) {
      console.error('Failed to fetch payment config:', error);
      toast.error('Failed to load payment configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggle = (key: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [key]: prev[key] === 'true' ? 'false' : 'true',
    }));
  };

  const hasOptionsChanges =
    selectedProvider !== initialProvider ||
    paymentConfigs.some((c) => editedValues[c.key] !== c.value);

  const handleSaveOptions = async () => {
    const changed = paymentConfigs.filter((c) => editedValues[c.key] !== c.value);
    const providerChanged = selectedProvider !== initialProvider;
    if (changed.length === 0 && !providerChanged) return;

    setSavingOptions(true);
    try {
      const promises = changed.map((c) =>
        configurationsService.updateConfiguration(c.key, editedValues[c.key])
      );

      if (providerChanged) {
        promises.push(
          configurationsService.updateConfiguration(PAYMENT_GATEWAY_CONFIG_KEY, selectedProvider)
        );
      }

      await Promise.all(promises);

      setPaymentConfigs((prev) =>
        prev.map((c) =>
          editedValues[c.key] !== undefined ? { ...c, value: editedValues[c.key] } : c
        )
      );
      setInitialProvider(selectedProvider);

      toast.success('Payment options saved successfully');
    } catch (error) {
      console.error('Failed to save payment options:', error);
      toast.error('Failed to save payment options');
    } finally {
      setSavingOptions(false);
    }
  };

  const hasCredentialChanges =
    gatewayConfig?.provider !== selectedProvider ||
    gatewayConfig?.api_key !== apiKey ||
    (gatewayConfig?.api_key_id || '') !== apiKeyId ||
    (gatewayConfig?.api_secret || '') !== apiSecret ||
    (gatewayConfig?.webhook_secret || '') !== webhookSecret;

  const handleSaveCredentials = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }
    if (!apiSecret.trim()) {
      toast.error('Please enter an API secret');
      return;
    }

    setSavingCredentials(true);
    try {
      const request: UpsertPaymentGatewayRequest = {
        provider: selectedProvider,
        api_key: apiKey,
        api_key_id: apiKeyId || undefined,
        api_secret: apiSecret,
        webhook_secret: webhookSecret || undefined,
      };

      const updatedConfig = await paymentGatewayService.upsertPaymentGatewayConfig(request);
      setGatewayConfig(updatedConfig);
      toast.success('API credentials saved successfully');
    } catch (error) {
      console.error('Failed to save API credentials:', error);
      toast.error('Failed to save API credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-56 mb-2" />
          <Skeleton className="h-4 w-80" />
        </div>
        {[...Array(2)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Payment Gateway</h2>
        <p className="text-muted-foreground">
          Configure your payment options and API credentials
        </p>
      </div>

      {/* Section 1: Payment Options (product backend) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <CreditCard className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>Payment Options</CardTitle>
                <CardDescription>
                  Select your payment provider and configure payment preferences
                </CardDescription>
              </div>
            </div>
            <Button
              size="sm"
              disabled={savingOptions || !hasOptionsChanges}
              onClick={handleSaveOptions}
            >
              {savingOptions ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="provider">Payment Gateway Provider</Label>
            <Select
              value={selectedProvider}
              onValueChange={(value: PaymentGatewayProvider) => setSelectedProvider(value)}
            >
              <SelectTrigger id="provider" className="w-full md:w-[300px]">
                <SelectValue placeholder="Select a provider" />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_PROVIDERS.map((provider) => (
                  <SelectItem key={provider.value} value={provider.value}>
                    {provider.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Choose the payment gateway you want to integrate with your store
            </p>
          </div>

          {paymentConfigs.length > 0 && (
            <>
              <div className="border-t pt-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {paymentConfigs.map((config) => (
                  <div key={config.key} className="space-y-2">
                    {isBooleanConfig(config) ? (
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <Label>{config.config_display_name || config.key}</Label>
                          {config.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {config.description}
                            </p>
                          )}
                        </div>
                        <Switch
                          checked={editedValues[config.key] === 'true'}
                          onCheckedChange={() => handleToggle(config.key)}
                        />
                      </div>
                    ) : (
                      <>
                        <Label htmlFor={config.key}>
                          {config.config_display_name || config.key}
                        </Label>
                        <Input
                          id={config.key}
                          value={editedValues[config.key] || ''}
                          onChange={(e) => handleValueChange(config.key, e.target.value)}
                          placeholder={config.description || ''}
                        />
                        {config.description && (
                          <p className="text-xs text-muted-foreground">{config.description}</p>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Section 2: API Credentials (order management backend) */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 rounded-lg">
              <Key className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <CardTitle>API Credentials</CardTitle>
              <CardDescription>
                Configure the API credentials for{' '}
                {PAYMENT_PROVIDERS.find((p) => p.value === selectedProvider)?.label}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="api-key">API Key *</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="api-key"
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your API key (client ID)"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Public identifier for your merchant account
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-key-id">API Key ID</Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="api-key-id"
                  value={apiKeyId}
                  onChange={(e) => setApiKeyId(e.target.value)}
                  placeholder="Enter your API key ID (optional)"
                  className="pl-10"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Additional key identifier used by some providers (e.g., Razorpay)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="api-secret">API Secret *</Label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="api-secret"
                  type={showApiSecret ? 'text' : 'password'}
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Enter your API secret (client secret)"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiSecret(!showApiSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showApiSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Private secret for authentication
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="webhook-secret">Webhook Secret</Label>
              <div className="relative">
                <Webhook className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="webhook-secret"
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder="Enter your webhook secret (optional)"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Secret used to verify webhook callback signatures
              </p>
            </div>
          </div>

          <div className="pt-2">
            <Button
              onClick={handleSaveCredentials}
              disabled={savingCredentials || !hasCredentialChanges || !apiKey.trim() || !apiSecret.trim()}
            >
              {savingCredentials ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Save Credentials
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
